<template>
  <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
  <div v-else-if="!computedMultimediaProps.is_removed" :class="{ 'rounded-2': isEditMode }" :style="multimediaStyle">
    <EditableComponent
      v-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
      :id="id"
      :active="componentEditMode && componentEditMode?.id === id"
      :collapsible="!isScribbleChild"
      :discard="shouldShowDiscardOption"
      :duplicate="componentPermissions.canDuplicate"
      :edit="componentPermissions.canEdit"
      :exclude-stylables="
        isScribbleChild
          ? [ThemeComponentOptionEnum.SPACING, ThemeComponentOptionEnum.PADDING, ThemeComponentOptionEnum.BORDER_RADIUS]
          : undefined
      "
      :parent-type="parentType"
      :remove="componentPermissions.canRemove"
      :resizable="isScribbleChild"
      :resize-handles="[
        'top-left',
        'top-right',
        'center-left',
        'center-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
      ]"
      :resize-maintain-aspect-ratio="hasMultimediaContent"
      :selected="componentSelectedMode && componentSelectedMode?.id === id"
      :settings="!usedInSectionId"
      :show-only-bottom-toolbar="shouldShowOnlyBottomToolbar"
      :show-only-top-toolbar="shouldShowOnlyTopToolbar"
      :stylable="!usedInSectionId"
      :style="editableComponentMultimediaStyle"
      :variants="
        componentPermissions.canEdit && !isScribbleChild
          ? {
              title: t('canvasUI.canvasBuilder.multimediaSettings.selectHorizontalAlignment'),
              options: horizontalAlignmentOptions,
              selected: data?.horizontal_alignment ?? MultimediaHorizontalAlignmentOptions.CENTER,
            }
          : undefined
      "
      @discard="discard()"
      @duplicate="duplicate(id)"
      @edit="onSelectFilesForMultimedia"
      @remove="removeComponent(id, usedInSectionId)"
      @select="setComponentSelectedMode(id)"
      @settings="setComponentSettingsMode(id)"
      @style="emit('style', $event)"
      @variant-change="onVariantChange"
    >
      <template v-if="computedMultimediaProps.data.type === 'video' && !isIosDevice" #toolbar-slot>
        <CPopover placement="bottom" trigger="click" :width="280">
          <template #trigger>
            <div class="flex items-center justify-center w-9 h-9 ml-1 cursor-pointer">
              <CIcon
                class="custom-icon !hover:color-primary2"
                :color="themeVars.primary"
                icon="clapperboard-play"
                size="16"
              />
            </div>
          </template>
          <div class="pa-2">
            <CBackgroundImageThemeOptions
              :background-image="data.poster_url"
              :background-image-placeholder="t('canvasUI.canvasBuilder.multimediaSettings.thumbnail')"
              :background-size="data.poster_background_size ?? 'cover'"
              :background-size-options="['cover', 'contain']"
              hide-background-attachment
              hide-background-position-x
              hide-background-position-y
              hide-background-repeat
              @bg-property-change="onBackgroundPropertyChange"
              @choose-content="chooseContent"
            />
          </div>
        </CPopover>
      </template>
      <RawMultimedia
        v-if="hasMultimediaContent"
        :id="id"
        :data="getModelValue(computedMultimediaProps.data)"
        :lazy="isDefaultDisplayMode"
        :parent-type="parentType"
        :selected="componentSelectedMode && componentSelectedMode?.id === id"
        :style="rawMultimediaStyle"
        :used-in-section-id="usedInSectionId"
        v-bind="attrs"
        @style="emit('style', $event)"
      />
      <EditMultimedia v-else v-bind="attrs" :is-scribble-child="isScribbleChild" />
    </EditableComponent>
    <RawMultimedia
      v-else
      :id="id"
      :data="getModelValue(computedMultimediaProps.data)"
      :lazy="isDefaultDisplayMode"
      :parent-type="parentType"
      :style="presentationModeStyle"
      :used-in-section-id="usedInSectionId"
    />
  </div>
</template>

<script setup lang="ts">
import { h, toRefs, onMounted, inject, computed, useAttrs, type ComputedRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { omit, pick } from 'lodash-es'
import type { DropdownOption } from 'naive-ui'

import { renderTemplate } from '@lib/util/handlebars'

import CBackgroundImageThemeOptions from '@lib/components/CBackgroundImageThemeOptions/CBackgroundImageThemeOptions.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import EditMultimedia from './Multimedia.edit.vue'
import RawMultimedia from './Multimedia.raw.vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasSectionOverrides from '@canvas-builder/composables/useCanvasSectionOverrides'
import useCrmShapeWithSectionContext from '@canvas-builder/composables/useCrmShapeWithSectionContext'
import {
  type MultimediaProps,
  type ComponentNode,
  ComponentTypes,
  MultimediaHorizontalAlignmentOptions,
} from '@canvas-builder/types/canvas'
import useThemeVars from '@lib/composables/useThemeVars'

import { type CanvasRetrieve, FileContentTypeEnum } from '@openapi'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { useComponentStyle } from '@canvas-builder/composables/useComponentStyle'
import useConfirmation from '@lib/composables/useConfirmation'
import { discardSectionComponentOverride, shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import useContentSelector from '@canvas-builder/composables/useContentSelector'
import type { AssetSelection, ExternalLinkSelection } from '@lib/types/selections'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { calculateProportionalDimensions } from '@canvas-builder/components/ui/Scribble/scribble.helpers'
import { useScribbleToolbar } from '@canvas-builder/composables/useScribbleToolbar'
import { ThemeComponentOptionEnum } from '@canvas-builder/composables/useCanvasTheme'
import type { UiSelectContentRequest, UiSelectContentResponse } from '@sdk/api/modules/ui/types.ui'
import type { BackgroundSize } from '@lib/types/background'
import { isIosDevice } from '@lib/util/userAgents'
import { useScribbleChild, getMultimediaScribbleConfig } from '@canvas-builder/composables/useScribbleChild'

const attrs = useAttrs()

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    data: MultimediaProps
    when_used_in_section?: ComponentNode['when_used_in_section']
    usedInSectionId?: CanvasRetrieve['id']
    visible?: ComponentNode['visible']
    parentType?: ComponentTypes
    tracking_id?: string
    style?: Record<string, any>
  }>(),
  {
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)
const emit = defineEmits<{
  style: [s: Record<string, any>]
}>()

const selectContent =
  inject<(payload?: UiSelectContentRequest) => Promise<UiSelectContentResponse>>('selectUserContent')

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const calculateDimensionsForScribble = () =>
  calculateProportionalDimensions(
    props.style?.width ? parseInt(props.style?.width as string) : 200,
    props.style?.height ? parseInt(props.style?.height as string) : 200,
    props.style?.width ? parseInt(props.style?.width as string) : 200,
  )

const { data, id, usedInSectionId, when_used_in_section } = toRefs(props)
const scribbleChildConfig = getMultimediaScribbleConfig(
  props.id,
  props.parentType,
  props.style,
  calculateDimensionsForScribble,
)
const { isScribbleChild, handleAddedToScribble, componentNode, createStyleUpdate } =
  useScribbleChild(scribbleChildConfig)
const { shouldShowOnlyTopToolbar, shouldShowOnlyBottomToolbar } = useScribbleToolbar(props.id)

const { t } = useI18n()
const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})
const {
  activeCanvas,
  componentEditMode,
  componentSelectedMode,
  duplicate,
  isDefaultDisplayMode,
  isEditMode,
  isViewOnlyMode,
  justAddedComponentId,
  mode,
  removeComponent,
  setComponentEditMode,
  setComponentSelectedMode,
  setComponentSettingsMode,
  sectionComponentOverrides,
  updateNodeDataById,
} = useCanvas()
const { discardOverridesConfirmationOptions, getComponentOverrides } = useCanvasSectionOverrides()
const { getDerivedStyleFromFeatureFlags } = useComponentStyle(componentNode)
const { fullContext } = useCrmShapeWithSectionContext()
const { setContentSelectorMode } = useContentSelector()
const confirmation = useConfirmation()
const themeVars = useThemeVars()

const horizontalAlignmentOptions = [
  {
    icon: () => h(CIcon, { icon: 'align-left', size: '18px' }),
    key: MultimediaHorizontalAlignmentOptions.LEFT,
    label: t('canvasUI.canvasBuilder.multimediaSettings.horizontalAlignment.left'),
  },
  {
    icon: () => h(CIcon, { icon: 'align-center', size: '18px' }),
    key: MultimediaHorizontalAlignmentOptions.CENTER,
    label: t('canvasUI.canvasBuilder.multimediaSettings.horizontalAlignment.center'),
  },
  {
    icon: () => h(CIcon, { icon: 'align-right', size: '18px' }),
    key: MultimediaHorizontalAlignmentOptions.RIGHT,
    label: t('canvasUI.canvasBuilder.multimediaSettings.horizontalAlignment.right'),
  },
]

const editableComponentMultimediaStyle = computed(() => {
  const style = getDerivedStyleFromFeatureFlags(props.style)
  return pick(style, 'borderTopLeftRadius', 'borderTopRightRadius', 'borderBottomLeftRadius', 'borderBottomRightRadius')
})

const hasMultimediaContent = computed(() => {
  return !!(computedMultimediaProps.value.data.asset_type === 'external'
    ? computedMultimediaProps.value.data.external_url
    : computedMultimediaProps.value.data.file?.content_url)
})

const rawMultimediaStyle = computed(() => {
  const style = getDerivedStyleFromFeatureFlags(props.style)

  return pick(
    style,
    'paddingBottom',
    'paddingLeft',
    'paddingTop',
    'paddingRight',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomLeftRadius',
    'borderBottomRightRadius',
    ...(isScribbleChild.value || !!launchDarkly.value.enable_resizable_multimedia_component ? ['width', 'height'] : []),
  )
})

async function chooseContent() {
  if (!selectContent) {
    console.error('selectContent is not provided')
    return
  }

  try {
    const data = await selectContent({
      allowed_selector_types: ['assets', 'external_links', 'ai_images', 'pexels_images', 'theme'],
      max_selections: 1,
      allowed_asset_content_types: [FileContentTypeEnum.IMAGE],
    })

    const newImageUrl = data?.content?.[0]?.thumbnail_url ?? (data?.content?.[0] as any)?.content_url

    if (newImageUrl) {
      updateNodeDataById(props.id, { ...props.data, poster_url: newImageUrl })
    }
  } catch (error) {
    console.error('Error in chooseContent:', error)
  }
}

function getSelections() {
  const selections: (AssetSelection | ExternalLinkSelection)[] = []
  const data = props.data

  if (data.asset_type === 'pitcher_asset' && data.file?.content_url) {
    selections.push({
      fileId: data.file.id,
      type: 'asset',
    })
  } else if (data.asset_type === 'external' && data.external_url) {
    selections.push({
      url: data.external_url,
      type: 'external_link',
      content_type: data.type,
      from: data.from ?? 'custom',
    })
  }

  return selections
}

function onBackgroundPropertyChange(property: string, value: any) {
  if (property === 'backgroundImage' && value === null) {
    updateNodeDataById(props.id, { ...props.data, poster_url: undefined, poster_background_size: 'cover' })
  }
  if (property === 'backgroundSize') {
    updateNodeDataById(props.id, { ...props.data, poster_background_size: value as BackgroundSize })
  }
}

const computedMultimediaProps = getComponentOverrides(data, id, usedInSectionId) as ComputedRef<{
  data: MultimediaProps
  is_removed: boolean
}>

const presentationModeStyle = computed(() => {
  const style = getDerivedStyleFromFeatureFlags(props.style)
  return !isEditMode.value || props.usedInSectionId ? style : undefined
})

const multimediaStyle = computed(() => {
  const style = getDerivedStyleFromFeatureFlags(props.style)
  if (!isEditMode.value) return undefined

  const omits = ['paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight']

  if (!isScribbleChild.value) {
    omits.push('top', 'left')
  }

  if (!launchDarkly.value.enable_resizable_multimedia_component) {
    omits.push('height', 'width')
  }

  return omit(style, ...omits)
})

const shouldShowDiscardOption = computed(() => {
  return !!(props.usedInSectionId && sectionComponentOverrides.value?.[props.usedInSectionId]?.[props.id])
})

const discard = () => {
  confirmation.warning(
    discardOverridesConfirmationOptions(t, () => {
      try {
        if (!activeCanvas.value?.id) {
          throw new Error('Canvas ID is not defined')
        }
        const updatedOverrides = discardSectionComponentOverride(
          props.usedInSectionId,
          props.id,
          sectionComponentOverrides.value,
        )

        // update section component overrides
        sectionComponentOverrides.value = updatedOverrides
      } catch (error) {
        console.error(error)
        return
      }
    }),
  )
}

function onVariantChange(selection?: DropdownOption['key']) {
  const variant = selection as MultimediaHorizontalAlignmentOptions
  updateNodeDataById(props.id, { ...props.data, horizontal_alignment: variant })
}

function onSelectFilesForMultimedia() {
  if (launchDarkly.value.show_asset_manager_for_multimedia) {
    setContentSelectorMode({
      id: props.id,
      componentType: ComponentTypes.Multimedia,
      selections: getSelections(),
      maxSelections: 1,
      allowedSelectorTypes: ['assets', 'external_links', 'ai_images', 'pexels_images', 'theme'],
      allowedAssetContentTypes: [FileContentTypeEnum.IMAGE, FileContentTypeEnum.VIDEO],
      usedInSectionId: props.usedInSectionId,
    })
  } else {
    setComponentEditMode(ComponentTypes.Multimedia, props.id, props.usedInSectionId)
  }
}
function getModelValue(value: MultimediaProps) {
  if (value.asset_type !== 'external') return value
  if (value.asset_type === 'external' && value.external_url) {
    // First try to parse with full context (including CRM shape), then fallback to plain URL
    try {
      return {
        ...value,
        external_url: renderTemplate(value.external_url, fullContext.value),
      }
    } catch (error) {
      console.warn('Failed to parse template in external URL, using original URL:', error)
      return {
        ...value,
        external_url: value.external_url,
      }
    }
  }
  return value
}

onMounted(() => {
  if (justAddedComponentId.value === props.id) {
    justAddedComponentId.value = null
    handleAddedToScribble(emit)
    onSelectFilesForMultimedia()
  }
})

// Watch for file changes in scribble children to update dimensions based on new file's aspect ratio
watch(
  () => {
    const data = computedMultimediaProps.value.data

    // Prevent from error when Multimedia node is deleted inside Scribble component
    if (!data) return

    return data.asset_type === 'external' ? data.external_url : data.file?.content_url
  },
  async (newUrl, oldUrl) => {
    // Only update dimensions for scribble children when the file URL actually changes
    if (!isScribbleChild.value || !newUrl || newUrl === oldUrl) return

    // Only proceed if we have existing dimensions to work with
    const currentStyle = props.style
    if (!currentStyle?.width) return

    const currentWidth = parseInt(currentStyle.width as string)
    if (isNaN(currentWidth)) return

    try {
      let naturalWidth: number
      let naturalHeight: number

      if (computedMultimediaProps.value.data.type === 'image') {
        // For images, load the image to get natural dimensions
        const img = new Image()
        img.src = newUrl

        const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
          img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight })
          }
          img.onerror = reject
        })

        naturalWidth = dimensions.width
        naturalHeight = dimensions.height
      } else {
        // For videos, we'll use a default aspect ratio since getting video dimensions
        // requires more complex handling. Most videos are 16:9
        naturalWidth = 16
        naturalHeight = 9
      }

      // Calculate new height based on current width and new aspect ratio
      const aspectRatio = naturalHeight / naturalWidth
      const newHeight = Math.round(currentWidth * aspectRatio)

      // Update the component's dimensions
      emit(
        'style',
        createStyleUpdate({
          width: `${currentWidth}px`,
          height: `${newHeight}px`,
        }),
      )
    } catch (error) {
      console.error('Error updating multimedia dimensions:', error)
    }
  },
)
</script>
